/**
 * MonthlyWrappedCard — mini-Wrapped surfaced during the first 7 days
 * of each new month (iter193 retention). Auto-hides for users who
 * joined mid-previous-month so we don't show "0 lectures in January"
 * to someone who signed up January 20.
 *
 * Feeds on /api/coach/monthly-wrapped → {visible, month_label, lectures,
 * cards_mastered, active_days, top_subject:{name,color,lectures}}.
 *
 * One-tap "Share" builds a 1080×1920 PNG client-side and either invokes
 * the Web Share API (mobile) or downloads (desktop) — same pattern as
 * the achievement share card so users learn one share gesture.
 *
 * Dismissible per-month via localStorage so the user isn't pestered all
 * 7 days if they tap "Close" on day 1.
 */
import { useEffect, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Sparkles, Share2, X, Award } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = 'isp_monthly_wrapped_dismissed';

const useMonthlyWrapped = () => useQuery({
  queryKey: ['monthly-wrapped'],
  queryFn: async () => (await axios.get(`${API}/coach/monthly-wrapped`)).data,
  staleTime: 5 * 60_000,
  refetchOnWindowFocus: false,
});

const buildShareCard = async (data, displayName) => {
  const W = 1080, H = 1920;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');

  const grad = ctx.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, '#002FA7');
  grad.addColorStop(1, '#10B981');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // Brand wordmark
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.font = 'bold 56px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui';
  ctx.textAlign = 'center';
  ctx.fillText('iStudyPRO', W / 2, 200);
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.font = '32px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui';
  ctx.fillText(`Mini-Wrapped · ${data.month_label}`, W / 2, 260);

  // Big numbers
  const rows = [
    { value: data.lectures, label: 'lectures recorded' },
    { value: data.cards_mastered, label: 'cards mastered' },
    { value: data.active_days, label: 'active days' },
  ];
  rows.forEach((row, i) => {
    const y = 540 + i * 280;
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 180px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui';
    ctx.fillText(String(row.value || 0), W / 2, y);
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.font = '36px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui';
    ctx.fillText(row.label, W / 2, y + 60);
  });

  if (data.top_subject?.name) {
    ctx.fillStyle = '#FFD700';
    ctx.font = 'bold 32px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui';
    ctx.fillText(`Top subject · ${data.top_subject.name}`, W / 2, H - 250);
  }
  if (displayName) {
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.font = '36px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui';
    ctx.fillText(`— ${displayName}`, W / 2, H - 160);
  }
  ctx.fillStyle = 'rgba(255,255,255,0.4)';
  ctx.font = '26px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui';
  ctx.fillText('istudypro.net', W / 2, H - 100);

  return new Promise((r) => canvas.toBlob(r, 'image/png', 0.92));
};

const MonthlyWrappedCard = () => {
  const { data, isLoading } = useMonthlyWrapped();
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!data?.month_label) return;
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (raw === data.month_label) setDismissed(true);
    } catch { /* ignore */ }
  }, [data?.month_label]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    if (data?.month_label) {
      try { localStorage.setItem(DISMISS_KEY, data.month_label); } catch { /* ignore */ }
    }
  }, [data?.month_label]);

  const share = useCallback(async () => {
    if (!data) return;
    try {
      const blob = await buildShareCard(data, user?.name);
      if (!blob) return;
      const file = new File([blob], `istudypro-${(data.month_label || 'wrapped').toLowerCase().replace(/\s+/g, '-')}.png`, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `My ${data.month_label} on iStudyPRO`,
          text: `Mini-Wrapped: ${data.lectures} lectures · ${data.cards_mastered} cards mastered · ${data.active_days} active days.`,
        });
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.warn('share failed:', e?.message);
    }
  }, [data, user?.name]);

  if (isLoading || !data?.visible || dismissed) return null;
  if (!data.lectures && !data.cards_mastered && !data.active_days) return null; // nothing to brag about

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-2xl border border-emerald-200 bg-gradient-to-br from-[#002FA7] via-[#1f3a8a] to-emerald-600 text-white p-5 sm:p-6 overflow-hidden mb-4"
      data-testid="monthly-wrapped-card"
    >
      <button
        type="button"
        onClick={dismiss}
        className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center transition-colors"
        aria-label="Dismiss this month's wrapped"
        data-testid="monthly-wrapped-dismiss"
      >
        <X className="w-3.5 h-3.5 text-white" />
      </button>

      <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-emerald-300 flex items-center gap-1.5">
        <Sparkles className="w-3 h-3" /> Mini-Wrapped · {data.month_label}
      </p>
      <h3 className="font-heading text-xl sm:text-2xl font-bold mt-1">
        Here's what {data.month_label.split(' ')[0]} looked like.
      </h3>

      <div className="grid grid-cols-3 gap-4 mt-5">
        <div>
          <div className="text-3xl sm:text-4xl font-bold">{data.lectures}</div>
          <div className="text-[11px] uppercase tracking-wide opacity-80 mt-0.5">Lectures</div>
        </div>
        <div>
          <div className="text-3xl sm:text-4xl font-bold">{data.cards_mastered}</div>
          <div className="text-[11px] uppercase tracking-wide opacity-80 mt-0.5">Cards mastered</div>
        </div>
        <div>
          <div className="text-3xl sm:text-4xl font-bold">{data.active_days}</div>
          <div className="text-[11px] uppercase tracking-wide opacity-80 mt-0.5">Active days</div>
        </div>
      </div>

      {data.top_subject?.name && (
        <p className="text-xs mt-4 opacity-90 flex items-center gap-1.5">
          <Award className="w-3.5 h-3.5 text-amber-300" />
          Top subject: <strong>{data.top_subject.name}</strong> · {data.top_subject.lectures} lecture{data.top_subject.lectures === 1 ? '' : 's'}
        </p>
      )}

      <button
        type="button"
        onClick={share}
        className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white text-[#002FA7] font-semibold text-sm hover:bg-amber-50 transition-colors"
        data-testid="monthly-wrapped-share"
      >
        <Share2 className="w-4 h-4" /> Share my month
      </button>
    </motion.div>
  );
};

export default MonthlyWrappedCard;
